// assets/birth-data-gate.js
// Blocks add-to-cart for personalized products until birth data is complete, so an order that
// can't be fulfilled (no chart) can never be placed. No-ops on products that don't collect birth
// data. The birth fields' presence is the single source of truth.
//
// Express "Buy it now" is handled separately (removed server-side in accelerated-checkout.liquid)
// because it bypasses the cart and drops line-item properties — it can't be made safe, only omitted.

import { hasBirthFields, isComplete, firstMissing, onBirthDataChange, BIRTH_SELECTORS } from './birth-data.js';

const FIELD_LABEL = { date: 'birth date', time: 'birth time', place: 'birthplace' };

class BirthDataGate extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    if (!hasBirthFields()) return; // not a personalized product → nothing to gate

    const formId = this.dataset.formId;
    this.form =
      (formId && document.getElementById(formId)) ||
      this.closest('product-form-component')?.querySelector('form') ||
      document.querySelector('form[data-type="add-to-cart-form"]');

    this.hint = /** @type {HTMLElement | null} */ (this.querySelector('[data-gate-hint]'));

    // Block the add-to-cart submit while incomplete (capture, before the form component handles it).
    this.form?.addEventListener('submit', (e) => this.onSubmit(e), true);
    // Clear the message once the fields are complete.
    onBirthDataChange(() => {
      if (isComplete() && this.hint) this.hint.hidden = true;
    });
    this.hidden = false;
  }

  /** @param {SubmitEvent} e */
  onSubmit(e) {
    if (isComplete()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.flag(firstMissing());
  }

  /** @param {string | null} missing */
  flag(missing) {
    if (!this.hint || !missing) return;
    const key = /** @type {'date' | 'time' | 'place'} */ (missing);
    this.hint.textContent = `Add your ${FIELD_LABEL[key] || 'birth details'} above to continue.`;
    this.hint.hidden = false;
    const sel = BIRTH_SELECTORS[key];
    const el = sel && /** @type {HTMLElement | null} */ (document.querySelector(sel));
    if (!el) return;
    // The time value lives in a hidden input (the inline Hour/Minute/AM-PM picker writes into it), and a
    // hidden input cannot take focus, so aim at its first visible control instead.
    const target = el.offsetParent === null
      ? ((el.closest('stz-time-picker, .__field') || el.parentElement)?.querySelector('select, input:not([type="hidden"]), textarea, button') || el)
      : el;
    /** @type {HTMLElement} */ (target).focus();
  }
}

if (!customElements.get('birth-data-gate')) {
  customElements.define('birth-data-gate', BirthDataGate);
}
